import React, { useCallback, useEffect, useState } from 'react';
import { ModalProps } from '@mui/base/Modal';
import { Modal } from '@/components';
import {
  IServiceRequest,
  ServiceRequestUpdateData,
} from '@/types/service-request-management.types';

export interface ServiceRequestEditModalProps
  extends Omit<ModalProps, 'children' | 'onSubmit'> {
  serviceRequest: IServiceRequest | null;
  formData: ServiceRequestUpdateData;
  setFormData: React.Dispatch<React.SetStateAction<ServiceRequestUpdateData>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onSubmit: (data: ServiceRequestUpdateData) => Promise<void>;
  isSubmitting?: boolean;
}

const serviceCategories = [
  { value: 'essentials', label: 'Essentials' },
  { value: 'finance', label: 'Finance' },
  { value: 'food_and_drink', label: 'Food and Drink' },
  { value: 'local', label: 'Local' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'medical', label: 'Medical' },
  { value: 'mobility', label: 'Mobility' },
  { value: 'sanitation', label: 'Sanitation' },
  { value: 'technical', label: 'Technical' },
  { value: 'transport', label: 'Transport' },
];

export const ServiceRequestEditModal = ({
  open,
  onClose,
  serviceRequest,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  className,
}: ServiceRequestEditModalProps) => {
  // Use local state to track form data
  const [localFormData, setLocalFormData] =
    useState<ServiceRequestUpdateData>(formData);

  // Initialize form data when modal opens
  useEffect(() => {
    if (serviceRequest && open) {
      const initialData = {
        name: serviceRequest.name,
        category: serviceRequest.category,
        description: serviceRequest.description,
      };
      setLocalFormData(initialData);
      setFormData(initialData);
      setErrors({});
    }
  }, [serviceRequest, open, setFormData, setErrors]);

  const handleChange = useCallback(
    (field: keyof ServiceRequestUpdateData, value: string) => {
      setLocalFormData((prev) => ({ ...prev, [field]: value }));
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    },
    [setFormData, setErrors, errors]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ensure parent formData is updated with latest localFormData before submission
    setFormData(localFormData);
    await onSubmit(localFormData);
  };

  const handleClose = () => {
    if (!isSubmitting && onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={className}
      aria-labelledby="service-request-edit-modal-title"
    >
      <div className="modal-content max-w-[600px] top-[5%]">
        <div className="modal-header">
          <h3 id="service-request-edit-modal-title" className="modal-title">
            Edit Service Request
          </h3>
          <button
            className="btn btn-xs btn-icon btn-light"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <i className="ki-outline ki-cross text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid gap-4">
              {/* Name Field */}
              <div className="flex flex-col gap-2">
                <label className="form-label text-gray-900">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`input ${errors.name ? 'input-error' : ''}`}
                  placeholder="Enter service request name"
                  value={localFormData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <span className="text-danger contrast:text-red-500 text-xs">
                    {errors.name}
                  </span>
                )}
              </div>

              {/* Category Field */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="service-request-category"
                  className="form-label text-gray-900"
                >
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  id="service-request-category"
                  className={`select ${errors.category ? 'select-error' : ''}`}
                  value={localFormData.category || ''}
                  onChange={(e) => handleChange('category', e.target.value)}
                  disabled={isSubmitting}
                  aria-invalid={!!errors.category}
                  aria-describedby={
                    errors.category
                      ? 'service-request-category-error'
                      : undefined
                  }
                >
                  <option value="">Select a category</option>
                  {serviceCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <span
                    id="service-request-category-error"
                    className="text-danger contrast:text-red-500 text-xs"
                  >
                    {errors.category}
                  </span>
                )}
              </div>

              {/* Description Field */}
              <div className="flex flex-col gap-2">
                <label className="form-label text-gray-900">Description</label>
                <textarea
                  className={`textarea ${errors.description ? 'textarea-error' : ''}`}
                  placeholder="Enter description"
                  rows={4}
                  value={localFormData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.description && (
                  <span className="text-danger contrast:text-red-500 text-xs">
                    {errors.description}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer justify-end gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
